import { applicationInsightKey, checkoutHost, isDevMode, paymentApiHost } from '@/core/infrastructure/environment';
import { ApplicationInsights, SeverityLevel, Snippet } from '@microsoft/applicationinsights-web';
import { IConfiguration } from '@microsoft/applicationinsights-core-js';
import { IConfig } from '@microsoft/applicationinsights-common';

export async function initAppInsight(): Promise<{
    infoLog: (data: any) => void,
    warnLog: (data: any) => void,
    errorLog: (data: any) => void,
    fatalLog: (data: any) => void,
    fatalException: (data: any, exception: any) => void,
}> {
    const toTrack = [
        'trackEvent',
        'trackPageView',
        // 'trackPageViewPerformance',
        'trackException',
        'trackTrace',
        'trackMetric',
        'trackDependencyData',
        'throwInternal', // called when a message is logged internally
        'logInternalMessage', // called when a message is logged internally
        'triggerSend', // called when data is queued to be sent to the server
        '_sender', // called when data is sent to the server
    ];

    const baseAppInsightConfig: IConfiguration & IConfig = {
        instrumentationKey: applicationInsightKey,
        disableFetchTracking: false,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAutoRouteTracking: true,
    };

    let appInsightSnippet: Promise<Snippet> | undefined;

    if (isDevMode()) {
        // Load debug module async in own chunk
        const debugModule = await import('@microsoft/applicationinsights-debugplugin-js');
        const DebugPlugin = debugModule.DebugPlugin;
        const debugPluginInstance = new DebugPlugin();
        appInsightSnippet = Promise.resolve({
            config: {
                ...baseAppInsightConfig,
                correlationHeaderDomains: stripProtocol([location.href]),
                extensions: [debugPluginInstance],
                extensionConfig: {
                    [DebugPlugin.identifier]: {
                        trackers: toTrack,
                    },
                },
            },
        });
    } else {
        appInsightSnippet = Promise.resolve({
            config: {
                ...baseAppInsightConfig,
                correlationHeaderDomains: stripProtocol([location.href, checkoutHost, paymentApiHost]),
            },
        });
    }

    function stripProtocol(domains: string[]): string[] {
        return domains.map(domain => domain.startsWith('http') ? new URL(domain).host : domain);
    }

    const snippet = await appInsightSnippet;
    const appInsights = new ApplicationInsights(snippet);

    appInsights.loadAppInsights();
    appInsights.trackPageView();

    function trackTrace(data: any, level: SeverityLevel) {
        appInsights.trackTrace({
            severityLevel: level,
            message: JSON.stringify(data),
        });
    }

    function trackException(data: any, exception: any) {
        appInsights.trackException({
            exception,
            severityLevel: SeverityLevel.Critical,
        }, {
            data: JSON.stringify(data),
        });
    }

    return {
        infoLog: (data: any) => trackTrace(data, SeverityLevel.Information),
        warnLog: (data: any) => trackTrace(data, SeverityLevel.Warning),
        errorLog: (data: any) => trackTrace(data, SeverityLevel.Error),
        fatalLog: (data: any) => trackTrace(data, SeverityLevel.Critical),
        fatalException: (data: any, exception: any) => trackException(data, exception),
    };
}
